import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { ChefHat, LogOut, Loader2, Users, Plus, Trash2, CheckCircle2, Clock, X, FileText, KeyRound, Camera, Sunrise, Moon, Activity, CalendarDays, History, MapPin, Pencil, Settings as SettingsIcon, Mail, ListChecks, Menu, Thermometer, AlertTriangle, BarChart3, ShieldCheck, UserCheck, Check, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import ProgressRing from "@/components/ProgressRing";
import NotificationBell from "@/components/NotificationBell";
import ScheduleManager from "@/components/ScheduleManager";
import HistoryModal, { StatusBadge, OnDutyLine, ItemBreakdown } from "@/components/HistoryModal";
import ChecklistEditor from "@/components/ChecklistEditor";
import PdfViewer from "@/components/PdfViewer";

function labelForKey(key) {
  const p = key.split(":");
  return p.length >= 2 ? p[1] : key;
}

export default function OwnerDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStaff, setShowStaff] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showExceptions, setShowExceptions] = useState(false);
  const [showLocations, setShowLocations] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [report, setReport] = useState(null); // { url, title, filename }
  const [locations, setLocations] = useState([]);
  const [activeLoc, setActiveLoc] = useState(localStorage.getItem("pb_location") || "all");
  const [scheduleFocus, setScheduleFocus] = useState(null);

  const loadLocations = useCallback(async () => {
    try { const { data } = await api.get("/locations"); setLocations(data.locations || []); } catch (e) { console.error("Failed to load locations", e); }
  }, []);
  useEffect(() => { loadLocations(); }, [loadLocations]);

  const switchLoc = (id) => {
    setActiveLoc(id);
    localStorage.setItem("pb_location", id);
    load();
  };

  const openSchedule = (name) => { setScheduleFocus(name || null); setShowSchedule(true); };

  const [onDuty, setOnDuty] = useState(null);
  const loadOnDuty = useCallback(async () => {
    if (activeLoc === "all") { setOnDuty(null); return; }
    try {
      const { data } = await api.get("/on-duty");
      setOnDuty(data);
      if ((!data.day?.current || !data.night?.current) && (data.supervisors || []).length > 0) {
        const key = `pb_onduty_nudge_${activeLoc}_${data.business_date}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, "1");
          toast.warning("Assign your Day and Night on-duty supervisors for today.", { icon: "🛎️", duration: 6000 });
        }
      }
    } catch (e) { setOnDuty(null); }
  }, [activeLoc]);
  useEffect(() => { loadOnDuty(); const t = setInterval(loadOnDuty, 8000); return () => clearInterval(t); }, [loadOnDuty]);

  const setOnDutySup = async (name, shift) => {
    const lbl = shift === "day" ? "Day" : "Night";
    try { await api.post("/on-duty/set", { supervisor_name: name || null, shift }); toast.success(name ? `${lbl} on-duty set` : `${lbl} on-duty cleared`); loadOnDuty(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const respondOnDuty = async (name, shift, approve) => {
    const lbl = shift === "day" ? "Day" : "Night";
    try { await api.post("/on-duty/respond", { supervisor_name: name, shift, approve }); toast.success(approve ? `${name} is now on ${lbl} duty` : "Request declined"); loadOnDuty(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const ackOverdue = async (checklistId, station) => {
    try { await api.post("/overdue/ack", { checklist_id: checklistId, station }); toast.success("Marked as handled"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const resetChecklist = async (cid) => {
    try {
      await api.post(`/checklists/${cid}/reset`);
      toast.success("Checklist cleared — starting fresh");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openReport = (sessionId, title, businessDate) => setReport({
    url: `/sessions/${sessionId}/report`,
    title: title || "Report",
    filename: `paiges-${title || "report"}-${businessDate || ""}.pdf`.replace(/\s+/g, "-").toLowerCase(),
  });

  const openReportUrl = (url, title, filename) => setReport({ url, title, filename });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0E0E0E]"><Loader2 className="h-8 w-8 animate-spin text-[#E11D2A]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0E0E0E] relative z-10">
      <header className="sticky top-0 z-20 border-b border-[#2E2E2E] bg-[#0E0E0E]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-[#E11D2A] flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-heading font-bold leading-none">Paige's Bistro</p>
              <p className="text-[11px] text-[#6B6B6B] mt-0.5">Owner Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2.5">
            <select data-testid="location-switcher" value={activeLoc} onChange={(e) => switchLoc(e.target.value)}
              className="h-10 sm:h-11 rounded-xl border border-[#2E2E2E] bg-[#171717] px-2 sm:px-3 text-xs sm:text-sm outline-none focus:border-[#E11D2A] max-w-[84px] sm:max-w-[160px]">
              <option value="all">All locations</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>

            {/* Desktop / tablet: full action row */}
            <div className="hidden sm:flex items-center gap-2.5">
              <button onClick={() => setShowLocations(true)} data-testid="manage-locations-button" className="h-11 px-4 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center gap-2 text-sm hover:border-[#E11D2A]/60 transition-colors">
                <MapPin className="h-4 w-4 text-[#A3A3A3]" /> <span className="hidden lg:inline">Locations</span>
              </button>
              <button onClick={() => setShowHistory(true)} data-testid="open-history-button" className="h-11 px-4 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center gap-2 text-sm hover:border-[#E11D2A]/60 transition-colors">
                <History className="h-4 w-4 text-[#A3A3A3]" /> <span className="hidden lg:inline">History</span>
              </button>
              <button onClick={() => setShowExceptions(true)} data-testid="open-temp-exceptions-button" className="h-11 px-4 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center gap-2 text-sm hover:border-[#E11D2A]/60 transition-colors" title="Temp exception log">
                <Thermometer className="h-4 w-4 text-[#A3A3A3]" /> <span className="hidden lg:inline">Temp Log</span>
              </button>
              <button onClick={() => openSchedule(null)} data-testid="manage-schedule-button" className="h-11 px-4 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center gap-2 text-sm hover:border-[#E11D2A]/60 transition-colors">
                <CalendarDays className="h-4 w-4 text-[#A3A3A3]" /> <span className="hidden lg:inline">Schedule</span>
              </button>
              <button onClick={() => setShowStaff(true)} data-testid="manage-staff-button" className="h-11 px-4 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center gap-2 text-sm hover:border-[#E11D2A]/60 transition-colors">
                <Users className="h-4 w-4 text-[#A3A3A3]" /> <span className="hidden lg:inline">Staff</span>
              </button>
              <button onClick={() => setShowEditor(true)} data-testid="edit-checklists-button" className="h-11 px-4 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center gap-2 text-sm hover:border-[#E11D2A]/60 transition-colors">
                <ListChecks className="h-4 w-4 text-[#A3A3A3]" /> <span className="hidden lg:inline">Checklists</span>
              </button>
              <button onClick={() => setShowSettings(true)} data-testid="owner-settings-button" className="h-11 w-11 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/60 transition-colors" title="Settings">
                <SettingsIcon className="h-4 w-4 text-[#A3A3A3]" />
              </button>
            </div>

            {/* Mobile: actions collapsed into a dropdown */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="owner-menu-button" className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/60 transition-colors" title="Menu">
                    <Menu className="h-4 w-4 text-[#A3A3A3]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-[#171717] border-[#2E2E2E] text-[#F5F5F5]">
                  <DropdownMenuItem data-testid="menu-locations" onSelect={() => setShowLocations(true)} className="gap-2.5 cursor-pointer focus:bg-[#232323] focus:text-white"><MapPin className="h-4 w-4 text-[#E11D2A]" /> Locations</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-history" onSelect={() => setShowHistory(true)} className="gap-2.5 cursor-pointer focus:bg-[#232323] focus:text-white"><History className="h-4 w-4 text-[#E11D2A]" /> History</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-temp-exceptions" onSelect={() => setShowExceptions(true)} className="gap-2.5 cursor-pointer focus:bg-[#232323] focus:text-white"><Thermometer className="h-4 w-4 text-[#E11D2A]" /> Temp Exceptions</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-schedule" onSelect={() => openSchedule(null)} className="gap-2.5 cursor-pointer focus:bg-[#232323] focus:text-white"><CalendarDays className="h-4 w-4 text-[#E11D2A]" /> Schedule</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-staff" onSelect={() => setShowStaff(true)} className="gap-2.5 cursor-pointer focus:bg-[#232323] focus:text-white"><Users className="h-4 w-4 text-[#E11D2A]" /> Staff</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-checklists" onSelect={() => setShowEditor(true)} className="gap-2.5 cursor-pointer focus:bg-[#232323] focus:text-white"><ListChecks className="h-4 w-4 text-[#E11D2A]" /> Checklists</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-settings" onSelect={() => setShowSettings(true)} className="gap-2.5 cursor-pointer focus:bg-[#232323] focus:text-white"><SettingsIcon className="h-4 w-4 text-[#E11D2A]" /> Settings</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <NotificationBell onAssign={openSchedule} />
            <button onClick={logout} data-testid="logout-button" className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/60 transition-colors">
              <LogOut className="h-4 w-4 text-[#A3A3A3]" />
            </button>
          </div>
        </div>
      </header>

      <main data-testid="owner-dashboard-container" className="max-w-6xl mx-auto px-4 sm:px-6 py-7 pb-24 fade-up">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">Tonight's checklists</h1>
        <p className="text-sm text-[#6B6B6B] mb-6">Live status across Opening, Kitchen Closing and FOH Closing.</p>

        {activeLoc !== "all" && onDuty && (
          <OnDutyCard onDuty={onDuty} onSet={setOnDutySup} onRespond={respondOnDuty} />
        )}

        {data.all_locations && data.location_overview && data.location_overview.length > 0 && (
          <div className="mb-7" data-testid="all-locations-overview">
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <MapPin className="h-4 w-4 text-[#E11D2A]" />
              <h2 className="font-heading text-lg font-semibold">All Locations Tonight</h2>
              <span className="text-xs text-[#6B6B6B]">· completion at a glance</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.location_overview.map((o) => (
                <button key={o.location_id} data-testid={`overview-card-${o.location_id}`} onClick={() => switchLoc(o.location_id)} className="text-left rounded-xl border border-[#2E2E2E] bg-[#171717] p-4 hover:border-[#E11D2A]/60 active:scale-[0.99] transition-all">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="font-medium text-sm truncate pr-2">{o.name}</span>
                    <span data-testid={`overview-pct-${o.location_id}`} className={`font-heading text-xl font-bold ${o.pct === 100 ? "text-[#16A34A]" : o.pct > 0 ? "text-[#E5A100]" : "text-[#6B6B6B]"}`}>{o.pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#0E0E0E] overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${o.pct === 100 ? "bg-[#16A34A]" : "bg-[#E11D2A]"}`} style={{ width: `${o.pct}%` }} />
                  </div>
                  <p className="text-xs text-[#6B6B6B] mt-2">{o.done}/{o.total} items complete tonight</p>
                  {o.recap && o.recap.assigned > 0 && (
                    <p data-testid={`overview-recap-${o.location_id}`} className={`text-[11px] mt-1 ${o.recap.completed >= o.recap.assigned ? "text-[#16A34A]" : "text-[#E5A100]"}`}>
                      Last night: {o.recap.completed} of {o.recap.assigned} checklists completed
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {data.station_board && data.station_board.length > 0 ? (
          <div className="mb-7" data-testid="station-board">
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <Activity className="h-4 w-4 text-[#E11D2A]" />
              <h2 className="font-heading text-lg font-semibold">Live Station Board</h2>
              <span className="text-xs text-[#6B6B6B]">· who's done vs still working tonight</span>
            </div>
            <div className="space-y-3">
              {data.station_board.map((b) => (
                <div key={b.checklist_id} className="rounded-xl border border-[#2E2E2E] bg-[#171717] p-4">
                  <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#6B6B6B] mb-2.5">
                    {b.shift === "opening" ? <Sunrise className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />} {b.title}{b.location_name ? ` · ${b.location_name}` : ""}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {b.stations.map((st) => <StationChip key={st.name} st={st} overdueMin={b.overdue_minutes} onAck={(station) => ackOverdue(b.checklist_id, station)} />)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <YesterdayAccountability today={data.today} onOpenHistory={() => setShowHistory(true)} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          {data.checklists.map((c) => (
            <ChecklistCard key={c.id} c={c} onReport={openReport} onReset={resetChecklist} />
          ))}
        </div>

        {/* Recent completed */}
        <h2 className="font-heading text-lg font-semibold mb-3">Recent Completed</h2>
        {data.recent.length === 0 ? (
          <p className="text-sm text-[#6B6B6B]">No completed checklists yet.</p>
        ) : (
          <div className="space-y-2">
            {data.recent.map((s) => (
              <div key={s.id} className="rounded-xl border border-[#2E2E2E] bg-[#171717] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#16A34A]" />
                  <div>
                    <p className="font-medium">{s.checklist_title}{s.location_name ? ` · ${s.location_name}` : ""} · {s.business_date}</p>
                    <p className="text-xs text-[#6B6B6B]">By {s.completed_by} · {s.progress.overall.done}/{s.progress.overall.total} items</p>
                    {s.handoff_note && <p data-testid={`recent-handoff-${s.id}`} className="text-xs text-[#E5A100] mt-0.5 italic">Handoff: “{s.handoff_note}”</p>}
                  </div>
                </div>
                <span className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 font-mono text-xs text-[#6B6B6B]">
                    <Clock className="h-3.5 w-3.5" />
                    {s.completed_at ? new Date(s.completed_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                  </span>
                  <button data-testid="download-report-button" onClick={() => openReport(s.id, `${s.checklist_title} · ${s.business_date}`, s.business_date)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-3 py-2 text-xs font-medium hover:border-[#E11D2A]/60 transition-colors">
                    <FileText className="h-3.5 w-3.5 text-[#E11D2A]" /> PDF
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </main>

      {showStaff && <StaffManager onClose={() => setShowStaff(false)} />}
      {showSchedule && <ScheduleManager highlightName={scheduleFocus} onClose={() => { setShowSchedule(false); setScheduleFocus(null); }} onSaved={load} />}
      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} />}
      {showExceptions && <TempExceptionsModal onClose={() => setShowExceptions(false)} onReport={openReportUrl} />}
      {showLocations && <LocationsModal onClose={() => setShowLocations(false)} onChanged={loadLocations} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showEditor && <ChecklistEditor onClose={() => setShowEditor(false)} onSaved={load} />}
      {report && <PdfViewer relUrl={report.url} title={report.title} filename={report.filename} onClose={() => setReport(null)} />}
    </div>
  );
}

function YesterdayAccountability({ today, onOpenHistory }) {
  const yISO = (d) => { try { const dt = new Date(d + "T00:00:00"); dt.setDate(dt.getDate() - 1); return dt.toISOString().slice(0, 10); } catch { return d; } };
  const minISO = (d) => { try { const dt = new Date(d + "T00:00:00"); dt.setDate(dt.getDate() - 30); return dt.toISOString().slice(0, 10); } catch { return d; } };
  const [date, setDate] = useState(() => yISO(today));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const openDetail = async (s) => {
    const key = `${s.location_id}:${s.checklist_id}`;
    if (openKey === key) { setOpenKey(null); setDetail(null); return; }
    setOpenKey(key); setDetail(null); setDetailBusy(true);
    try {
      const p = new URLSearchParams({ checklist_id: s.checklist_id, date, location_id: s.location_id || "" });
      const { data } = await api.get(`/history/breakdown?${p.toString()}`);
      setDetail(data);
    } catch (e) { setDetail(null); } finally { setDetailBusy(false); }
  };
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/history?date_from=${date}&date_to=${date}`); setRows(data.sessions || []); }
    catch (e) { setRows([]); } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const isYesterday = date === yISO(today);
  return (
    <div className="mb-7" data-testid="yesterday-accountability">
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <History className="h-4 w-4 text-[#E11D2A]" />
        <h2 className="font-heading text-lg font-semibold">{isYesterday ? "Yesterday's" : "Day"} Accountability</h2>
        <input type="date" data-testid="accountability-date" value={date} min={minISO(today)} max={yISO(today)} onChange={(e) => e.target.value && setDate(e.target.value)}
          className="h-8 rounded-lg border border-[#2E2E2E] bg-[#171717] px-2 text-xs outline-none focus:border-[#E11D2A]" />
        <button data-testid="open-full-history-button" onClick={onOpenHistory} className="text-xs text-[#A3A3A3] hover:text-[#E11D2A] underline underline-offset-2">Full history →</button>
      </div>
      {loading ? (
        <div className="rounded-xl border border-[#2E2E2E] bg-[#171717] p-4 text-sm text-[#6B6B6B]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-[#2E2E2E] bg-[#171717] p-4 text-sm text-[#6B6B6B]">No checklists for this day.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {rows.map((s) => {
            const key = `${s.location_id}:${s.checklist_id}`;
            const open = openKey === key;
            return (
            <div key={s.id} data-testid={`accountability-row-${s.checklist_id}`} onClick={() => openDetail(s)}
              className={`rounded-xl border bg-[#171717] p-4 cursor-pointer transition-colors ${open ? "border-[#E11D2A]/60" : "border-[#2E2E2E] hover:border-[#E11D2A]/40"}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="font-medium text-sm truncate">{s.checklist_title}{s.location_name ? ` · ${s.location_name}` : ""}</span>
                <StatusBadge status={s.status} />
              </div>
              <div className="h-1.5 rounded-full bg-[#0E0E0E] overflow-hidden">
                <div className={`h-full rounded-full ${s.status === "completed" ? "bg-[#16A34A]" : s.done > 0 ? "bg-[#E5A100]" : "bg-[#3a3a3a]"}`} style={{ width: `${s.total ? (s.done / s.total) * 100 : 0}%` }} />
              </div>
              <p className="text-[11px] text-[#6B6B6B] mt-1.5">{s.done}/{s.total} items{s.completed_by ? ` · finalized by ${s.completed_by}` : ""}</p>
              <OnDutyLine onDuty={s.on_duty} />
              {s.contributors && s.contributors.length > 0 && (
                <p className="text-[11px] text-[#A3A3A3] mt-1"><span className="text-[#6B6B6B]">Checked off by:</span> {s.contributors.join(", ")}</p>
              )}
              {s.missing && s.missing.length > 0 && (
                <p data-testid="accountability-missing" className="text-[11px] text-[#EF4444] mt-1">Still missing: {s.missing.join(", ")}</p>
              )}
              <p className="text-[10px] text-[#6B6B6B] mt-1.5 flex items-center gap-1">
                <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide items" : "Tap to see items by station"}
              </p>
              {open && <ItemBreakdown loading={detailBusy} detail={detail} />}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OnDutyCard({ onDuty, onSet, onRespond }) {
  const sups = onDuty.supervisors || [];
  const requests = onDuty.requests || [];
  const needsAssign = (!onDuty.day?.current || !onDuty.night?.current) && sups.length > 0;
  const Row = ({ label, shift, slot, Icon }) => (
    <div data-testid={`on-duty-row-${shift}`} className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#E11D2A]" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] w-10">{label}</span>
        {slot?.current
          ? <span data-testid={`on-duty-current-${shift}`} className="inline-flex items-center gap-2 rounded-full border border-[#16A34A]/40 bg-[#16A34A]/10 px-3 py-1 text-sm font-semibold text-[#16A34A]"><UserCheck className="h-4 w-4" /> {slot.current.name}</span>
          : <span data-testid={`on-duty-current-${shift}`} className="text-sm text-[#6B6B6B]">Unassigned</span>}
      </div>
      <div className="flex items-center gap-2">
        <select data-testid={`on-duty-select-${shift}`} value="" onChange={(e) => { if (e.target.value) onSet(e.target.value, shift); }}
          className="h-9 rounded-lg border border-[#2E2E2E] bg-[#171717] px-2 text-xs outline-none focus:border-[#E11D2A]">
          <option value="">{slot?.current ? "Change…" : "Assign…"}</option>
          {sups.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
        {slot?.current && <button data-testid={`on-duty-clear-${shift}`} onClick={() => onSet(null, shift)} className="h-9 px-3 rounded-lg border border-[#2E2E2E] bg-[#171717] text-xs hover:border-[#E11D2A]/60 transition-colors">Clear</button>}
      </div>
    </div>
  );
  return (
    <div className="mb-7" data-testid="on-duty-card">
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <ShieldCheck className="h-4 w-4 text-[#E11D2A]" />
        <h2 className="font-heading text-lg font-semibold">Supervisors On Duty</h2>
        <span className="text-xs text-[#6B6B6B]">· Day &amp; Night · resets at 7 AM</span>
      </div>
      <div className="rounded-xl border border-[#2E2E2E] bg-[#171717] p-4 space-y-3">
        {needsAssign && (
          <div data-testid="on-duty-nudge" className="flex items-start gap-2 rounded-lg border border-[#E5A100]/40 bg-[#E5A100]/[0.08] px-3 py-2.5 text-sm text-[#E5A100] gold-pulse">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Assign today's on-duty supervisors. The Day supervisor handles the Opening checklist; the Night supervisor handles the Closing checklists.</span>
          </div>
        )}
        <Row label="Day" shift="day" slot={onDuty.day} Icon={Sunrise} />
        <Row label="Night" shift="night" slot={onDuty.night} Icon={Moon} />
        {sups.length === 0 && (
          <p className="text-xs text-[#6B6B6B]">No supervisors are assigned to this location yet. Add one in Staff and assign them to this location.</p>
        )}
        {requests.length > 0 && (
          <div className="border-t border-[#242424] pt-3 space-y-2" data-testid="on-duty-requests">
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B]">Pending on-duty requests</p>
            {requests.map((r) => (
              <div key={`${r.name}-${r.shift}`} data-testid={`on-duty-request-${r.name}-${r.shift}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E5A100]/30 bg-[#E5A100]/[0.06] px-3 py-2">
                <span className="text-sm font-medium text-[#F5F5F5] flex items-center gap-2">{r.name}<span className="text-[10px] font-mono uppercase tracking-widest text-[#E5A100]">{r.shift}</span></span>
                <span className="flex items-center gap-2">
                  <button data-testid={`approve-on-duty-${r.name}-${r.shift}`} onClick={() => onRespond(r.name, r.shift, true)}
                    className="inline-flex items-center gap-1 rounded-lg bg-[#16A34A] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#15803D] transition-colors">
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button data-testid={`decline-on-duty-${r.name}-${r.shift}`} onClick={() => onRespond(r.name, r.shift, false)}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-3 py-1.5 text-xs font-semibold hover:border-[#E11D2A]/60 transition-colors">
                    <X className="h-3.5 w-3.5" /> Decline
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const STAT = {
  done: { dot: "bg-[#16A34A]", ring: "border-[#16A34A]/40 bg-[#16A34A]/[0.08]", txt: "text-[#16A34A]" },
  working: { dot: "bg-[#F59E0B]", ring: "border-[#F59E0B]/40 bg-[#F59E0B]/[0.08]", txt: "text-[#F59E0B]" },
  waiting: { dot: "bg-[#6B6B6B]", ring: "border-[#2E2E2E] bg-[#0E0E0E]", txt: "text-[#6B6B6B]" },
};

function fmtDur(ms) {
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function StationChip({ st, overdueMin = 45, onAck }) {
  const elapsed = st.started_at ? Date.now() - new Date(st.started_at).getTime() : 0;
  const isLate = st.status === "working" && st.started_at && elapsed >= overdueMin * 60000;
  const overdue = isLate && !st.acked;
  const s = overdue
    ? { dot: "bg-[#EF4444]", ring: "border-[#EF4444]/60 bg-[#EF4444]/[0.12]", txt: "text-[#EF4444]" }
    : (STAT[st.status] || STAT.waiting);
  let timeLabel = null;
  if (st.status === "waiting") timeLabel = "not started";
  else if (st.status === "working" && st.started_at) timeLabel = overdue ? `${fmtDur(elapsed)} — overdue` : (st.acked && isLate ? `${fmtDur(elapsed)} · handled` : `${fmtDur(elapsed)} on section`);
  else if (st.status === "done" && st.started_at && st.ended_at) timeLabel = `took ${fmtDur(new Date(st.ended_at).getTime() - new Date(st.started_at).getTime())}`;
  return (
    <div data-testid={overdue ? "station-chip-overdue" : `station-chip-${st.status}`} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${s.ring}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot} ${st.status === "working" ? "animate-pulse" : ""}`} />
      <span className="text-sm font-medium">{st.name}</span>
      {st.sections?.length > 0 && <span className="text-[11px] text-[#6B6B6B] hidden sm:inline">{st.sections.join(", ")}</span>}
      <span className={`text-[11px] font-mono ${s.txt}`}>{st.done}/{st.total}</span>
      {timeLabel && (
        <span data-testid={`station-time-${st.status}`} className={`inline-flex items-center gap-1 text-[10px] font-mono pl-1.5 border-l border-[#2E2E2E] ${overdue ? "text-[#EF4444] font-semibold" : "text-[#6B6B6B]"}`}>
          {overdue ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{timeLabel}
        </span>
      )}
      {overdue && onAck && (
        <button data-testid={`station-ack-${st.name}`} onClick={() => onAck(st.name)} className="ml-0.5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold px-2 py-0.5 hover:bg-[#B91C1C] transition-colors">Ack</button>
      )}
    </div>
  );
}

function ChecklistCard({ c, onReport, onReset }) {  const active = c.active;
  const prog = active.progress;
  const overall = prog.overall;
  const isComplete = active.status === "completed";
  const pct = overall.total ? Math.round((overall.done / overall.total) * 100) : 0;
  const Icon = c.shift === "opening" ? Sunrise : Moon;
  const photos = active.photos || {};
  const photoEntries = Object.entries(photos);
  const token = localStorage.getItem("pb_token");
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const hasProgress = overall.done > 0 || photoEntries.length > 0 || (active.signoffs?.length > 0) || isComplete;
  const doReset = async () => {
    setClearing(true);
    try { await onReset(c.id); } finally { setClearing(false); setConfirming(false); }
  };

  return (
    <div data-testid={`checklist-card-${c.id}`} className={`rounded-2xl border p-5 ${isComplete ? "border-[#16A34A]/40 bg-[#16A34A]/[0.06]" : "border-[#2E2E2E] bg-[#171717]"}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isComplete ? "bg-[#16A34A]/15" : "bg-[#232323]"}`}>
            <Icon className={`h-5 w-5 ${isComplete ? "text-[#16A34A]" : "text-[#E11D2A]"}`} />
          </div>
          <div>
            <p className="font-heading font-semibold leading-none">{c.title}</p>
            <span className={`text-[11px] font-mono uppercase tracking-wide ${isComplete ? "text-[#16A34A]" : "text-[#E11D2A]"}`}>
              {isComplete ? "Completed" : "In progress"}
            </span>
          </div>
        </div>
        <ProgressRing done={overall.done} total={overall.total} size={48} stroke={5} />
      </div>

      <div className="h-2 rounded-full bg-[#232323] overflow-hidden mb-3">
        <div className={`h-full transition-all duration-500 ${isComplete ? "bg-[#16A34A]" : "bg-gradient-to-r from-[#E11D2A] to-[#EF4444]"}`} style={{ width: `${pct}%` }} />
      </div>

      <div className="space-y-1.5 mb-4">
        {prog.sections.map((s) => (
          <div key={s.id} className="flex items-center justify-between text-xs">
            <span className="text-[#A3A3A3]">{s.title}</span>
            <span className={`font-mono ${s.done === s.total ? "text-[#16A34A]" : "text-[#6B6B6B]"}`}>{s.done}/{s.total}</span>
          </div>
        ))}
      </div>

      {isComplete && <p className="text-xs text-[#6B6B6B] mb-3">Verified by {active.completed_by}</p>}
      {active.override_note && <p className="text-xs text-[#EF4444] mb-3">Override: {active.override_note}</p>}

      {photoEntries.length > 0 && (
        <div className="mb-4">
          <p className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-[#6B6B6B] mb-2"><Camera className="h-3.5 w-3.5" /> Photo proof ({photoEntries.length})</p>
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid={`photo-proof-${c.id}`}>
            {photoEntries.map(([key, p]) => {
              const url = `${API}/files/${p.path}?auth=${token}`;
              return (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <img src={url} alt="proof" className="h-14 w-14 rounded-lg object-cover border border-[#2E2E2E] hover:border-[#E11D2A] transition-colors" title={`${labelForKey(key)} · ${p.by}`} />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {active.signoffs?.length > 0 && (
        <p className="text-xs text-[#6B6B6B] mb-3">{active.signoffs.length} crew sign-off{active.signoffs.length > 1 ? "s" : ""} recorded</p>
      )}

      <button data-testid={`download-report-${c.id}`} onClick={() => onReport(active.id, c.title, active.business_date)} className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-4 py-2.5 text-sm font-medium hover:border-[#E11D2A]/60 transition-colors">
        <FileText className="h-4 w-4 text-[#E11D2A]" /> View PDF report
      </button>

      {hasProgress && (
        confirming ? (
          <div data-testid={`clear-confirm-${c.id}`} className="mt-2.5 rounded-lg border border-[#EF4444]/50 bg-[#EF4444]/10 p-3">
            <p className="text-xs text-[#F5F5F5] mb-2.5">Wipe all check-offs, readings, photos & sign-offs for <b>{c.title}</b> today? This can't be undone.</p>
            <div className="flex gap-2">
              <button data-testid={`clear-confirm-yes-${c.id}`} onClick={doReset} disabled={clearing} className="flex-1 rounded-lg bg-[#EF4444] text-white text-xs font-semibold py-2 hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
                {clearing ? "Clearing…" : "Yes, clear it"}
              </button>
              <button data-testid={`clear-confirm-cancel-${c.id}`} onClick={() => setConfirming(false)} className="flex-1 rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] text-xs font-medium py-2 hover:border-[#6B6B6B] transition-colors">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button data-testid={`clear-checklist-${c.id}`} onClick={() => setConfirming(true)} className="mt-2.5 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-4 py-2 text-xs font-medium text-[#A3A3A3] hover:border-[#EF4444]/60 hover:text-[#EF4444] transition-colors">
            <Trash2 className="h-3.5 w-3.5" /> Clear / reset today
          </button>
        )
      )}
    </div>
  );
}

function ExceptionTrends({ rows }) {
  const counts = {};
  rows.forEach((r) => { const k = r.item || "Unit"; counts[k] = (counts[k] || 0) + 1; });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const max = sorted[0]?.[1] || 1;
  return (
    <div data-testid="temp-exceptions-trends" className="rounded-xl border border-[#2E2E2E] bg-[#0E0E0E] p-4 mb-1">
      <p className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-[#6B6B6B] mb-3"><BarChart3 className="h-3.5 w-3.5 text-[#E11D2A]" /> Most frequent out-of-range units</p>
      <div className="space-y-2">
        {sorted.map(([name, count]) => (
          <div key={name} className="flex items-center gap-2">
            <span className="w-28 sm:w-36 shrink-0 truncate text-xs text-[#A3A3A3]" title={name}>{name}</span>
            <div className="flex-1 h-4 rounded bg-[#171717] overflow-hidden"><div className="h-full bg-[#E11D2A] rounded transition-all duration-500" style={{ width: `${(count / max) * 100}%` }} /></div>
            <span className="w-6 text-right text-xs font-mono text-[#EF4444]">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TempExceptionsModal({ onClose, onReport }) {
  const fmtDate = (d) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(fmtDate(new Date(Date.now() - 6 * 86400000)));
  const [end, setEnd] = useState(fmtDate(new Date()));
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/temp-exceptions?start=${start}&end=${end}`); setRows(data.exceptions || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);
  const download = () => onReport(`/temp-exceptions/report?start=${start}&end=${end}`, "Temp Exception Log", `temp-exceptions-${start}-to-${end}.pdf`);
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="temp-exceptions-modal" className="w-full max-w-2xl rounded-2xl border border-[#2E2E2E] bg-[#171717] max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E]">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Thermometer className="h-5 w-5 text-[#E11D2A]" /> Temp Exception Log</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 border-b border-[#2E2E2E]">
          <p className="text-xs text-[#6B6B6B] mb-3">Every out-of-range temperature reading and its corrective action — for health-inspector prep.</p>
          <div className="flex flex-wrap items-end gap-2.5">
            <label className="text-[11px] text-[#A3A3A3]">From
              <input data-testid="temp-exceptions-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 block rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2 text-sm text-white outline-none focus:border-[#E11D2A]" />
            </label>
            <label className="text-[11px] text-[#A3A3A3]">To
              <input data-testid="temp-exceptions-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 block rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2 text-sm text-white outline-none focus:border-[#E11D2A]" />
            </label>
            <button data-testid="temp-exceptions-run" onClick={run} disabled={busy} className="rounded-lg bg-[#E11D2A] text-white font-semibold px-4 py-2 text-sm hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">{busy ? "Loading…" : "Run"}</button>
            <button data-testid="temp-exceptions-download" onClick={download} className="rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:border-[#E11D2A]/60 transition-colors"><FileText className="h-4 w-4 text-[#E11D2A]" /> PDF</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2.5">
          {rows && rows.length > 0 && <ExceptionTrends rows={rows} />}
          {rows === null ? (
            <div className="flex items-center justify-center py-10 text-[#6B6B6B]"><Loader2 className="h-6 w-6 animate-spin text-[#E11D2A]" /></div>
          ) : rows.length === 0 ? (
            <div data-testid="temp-exceptions-empty" className="rounded-xl border border-[#16A34A]/40 bg-[#16A34A]/10 px-4 py-6 text-center text-sm text-[#16A34A]">No out-of-range readings in this period.</div>
          ) : rows.map((r, i) => (
            <div key={i} data-testid="temp-exceptions-row" className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/[0.06] p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm font-medium">{r.checklist_title}: {r.item}{r.location_name ? ` · ${r.location_name}` : ""}</span>
                <span className="font-mono text-sm"><span className="text-[#EF4444] font-bold">{r.value}</span> <span className="text-[#6B6B6B]">/ target {r.expected}</span></span>
              </div>
              <p className="text-[11px] text-[#6B6B6B] mt-1">{fmtTime(r.checked_at)}{r.checked_by ? ` · ${r.checked_by}` : ""} · {r.business_date}</p>
              <p className={`text-xs mt-2 ${r.corrective ? "text-[#E5A100]" : "text-[#EF4444]"}`}>Corrective: {r.corrective || "— none recorded —"}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LocationsModal({ onClose, onChanged }) {
  const TZ_OPTIONS = [
    { v: "America/New_York", l: "Eastern (New York)" },
    { v: "America/Chicago", l: "Central (Chicago)" },
    { v: "America/Denver", l: "Mountain (Denver)" },
    { v: "America/Phoenix", l: "Mountain – no DST (Phoenix)" },
    { v: "America/Los_Angeles", l: "Pacific (Los Angeles)" },
    { v: "America/Anchorage", l: "Alaska (Anchorage)" },
    { v: "Pacific/Honolulu", l: "Hawaii (Honolulu)" },
  ];
  const [locations, setLocations] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const { data } = await api.get("/locations"); setLocations(data.locations || []); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return toast.error("Enter a location name");
    setBusy(true);
    try { await api.post("/locations", { name: name.trim() }); setName(""); await load(); onChanged?.(); toast.success("Location added"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/locations/${id}`); await load(); onChanged?.(); toast.success("Location removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const changeTz = async (id, tz) => {
    setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, timezone: tz } : l)));
    try { await api.patch(`/locations/${id}`, { timezone: tz }); toast.success("Timezone updated"); }
    catch (e) { toast.error(apiErr(e)); await load(); }
  };
  const changeOverdue = async (id, minutes) => {
    setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, overdue_minutes: minutes } : l)));
    const m = parseInt(minutes, 10);
    if (!m || m < 5 || m > 600) return;
    try { await api.patch(`/locations/${id}`, { overdue_minutes: m }); }
    catch (e) { toast.error(apiErr(e)); await load(); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-[#2E2E2E] bg-[#171717]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E]">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><MapPin className="h-5 w-5 text-[#E11D2A]" /> Locations</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <input data-testid="location-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New location name" className="flex-1 rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
            <button data-testid="location-add-button" onClick={add} disabled={busy} className="rounded-lg bg-[#E11D2A] text-white font-semibold px-4 text-sm hover:bg-[#B91C1C] disabled:opacity-60 transition-colors flex items-center gap-1.5"><Plus className="h-4 w-4" /> Add</button>
          </div>
          <div className="space-y-2">
            {locations.map((l) => (
              <div key={l.id} data-testid={`location-row-${l.id}`} className="rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{l.name}</span>
                  <button onClick={() => remove(l.id)} className="h-8 w-8 rounded-lg hover:bg-[#E11D2A]/15 flex items-center justify-center text-[#A3A3A3] hover:text-[#E11D2A] transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
                <label className="mt-2.5 flex items-center gap-2 text-[11px] text-[#A3A3A3]">
                  <Clock className="h-3.5 w-3.5 text-[#E11D2A]" /> Report timezone
                  <select
                    data-testid={`location-timezone-${l.id}`}
                    value={l.timezone || "America/New_York"}
                    onChange={(e) => changeTz(l.id, e.target.value)}
                    className="ml-auto rounded-md bg-[#171717] border border-[#2E2E2E] px-2 py-1.5 text-xs text-white outline-none focus:border-[#E11D2A]"
                  >
                    {TZ_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </label>
                <label className="mt-2 flex items-center gap-2 text-[11px] text-[#A3A3A3]">
                  <AlertTriangle className="h-3.5 w-3.5 text-[#E11D2A]" /> Overdue after
                  <input
                    type="number" min="5" max="600"
                    data-testid={`location-overdue-${l.id}`}
                    value={l.overdue_minutes ?? 45}
                    onChange={(e) => changeOverdue(l.id, e.target.value)}
                    className="ml-auto w-20 rounded-md bg-[#171717] border border-[#2E2E2E] px-2 py-1.5 text-xs text-white outline-none focus:border-[#E11D2A]"
                  />
                  <span className="text-[#6B6B6B]">min</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ onClose }) {
  const { refreshUser } = useAuth();
  const [alertEmail, setAlertEmail] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/settings"); setAlertEmail(data.alert_email || ""); setOwnerName(data.owner_name || ""); setRecipients(data.digest_recipients || []); } catch (e) { toast.error(apiErr(e)); } })();
  }, []);
  const addRecipient = () => {
    const e = newRecipient.trim();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return toast.error("Enter a valid email");
    if (recipients.includes(e)) return toast.error("Already added");
    setRecipients((r) => [...r, e]); setNewRecipient("");
  };
  const save = async () => {
    setBusy(true);
    try { await api.put("/settings", { alert_email: alertEmail, owner_name: ownerName, digest_recipients: recipients }); await refreshUser(); toast.success("Settings saved"); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-[#2E2E2E] bg-[#171717] max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E]">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><SettingsIcon className="h-5 w-5 text-[#E11D2A]" /> Owner Settings</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5">Your name</label>
            <input data-testid="settings-owner-name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5"><Mail className="h-3 w-3" /> Notification email</label>
            <input data-testid="settings-alert-email" type="email" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} placeholder="alerts@yourbistro.com" className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
            <p className="text-[11px] text-[#6B6B6B] mt-1.5">Completion alerts, assignment requests and the 7 AM digest are sent here.</p>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5"><Users className="h-3 w-3" /> Extra digest recipients</label>
            <p className="text-[11px] text-[#6B6B6B] mb-2">Managers who should also get the 7 AM morning digest.</p>
            <div className="flex gap-2">
              <input data-testid="digest-recipient-input" type="email" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }} placeholder="manager@yourbistro.com" className="flex-1 rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
              <button data-testid="digest-recipient-add" onClick={addRecipient} className="rounded-lg bg-[#E11D2A] text-white font-semibold px-4 text-sm hover:bg-[#B91C1C] transition-colors flex items-center gap-1.5"><Plus className="h-4 w-4" /></button>
            </div>
            {recipients.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {recipients.map((e) => (
                  <div key={e} data-testid={`digest-recipient-${e}`} className="flex items-center justify-between rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-3 py-2 text-sm">
                    <span className="truncate">{e}</span>
                    <button onClick={() => setRecipients((r) => r.filter((x) => x !== e))} className="h-7 w-7 rounded-lg hover:bg-[#E11D2A]/15 flex items-center justify-center text-[#A3A3A3] hover:text-[#E11D2A] transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button data-testid="settings-save-button" onClick={save} disabled={busy} className="w-full rounded-xl bg-[#E11D2A] text-white font-heading font-bold py-3 hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">{busy ? "Saving…" : "Save Settings"}</button>
        </div>
      </div>
    </div>
  );
}

function StaffManager({ onClose }) {
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ name: "", pin: "", role: "employee", area: "kitchen", station: "", location_ids: [] });
  const [busy, setBusy] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try {
      const [{ data: st }, { data: ld }] = await Promise.all([api.get("/staff"), api.get("/locations")]);
      setStaff(st); setLocations(ld.locations || []);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleLoc = (id) => setForm((f) => ({ ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id] }));

  const startEdit = (s) => { setResetId(null); setEditId(s.id); setEdit({ name: s.name, area: s.area === "all" ? "kitchen" : s.area, role: s.role, location_ids: s.location_ids || [] }); };
  const toggleEditLoc = (id) => setEdit((e) => ({ ...e, location_ids: e.location_ids.includes(id) ? e.location_ids.filter((x) => x !== id) : [...e.location_ids, id] }));
  const saveEdit = async (id) => {
    if (!edit.name.trim()) return toast.error("Name cannot be empty");
    try { await api.put(`/staff/${id}`, edit); toast.success("Staff updated"); setEditId(null); setEdit(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const add = async () => {
    if (!form.name.trim() || form.pin.length < 4) return toast.error("Name and 4+ digit PIN required");
    if (form.location_ids.length === 0) return toast.error("Assign at least one location");
    setBusy(true);
    try {
      await api.post("/staff", { ...form, station: form.station || null });
      toast.success("Staff added");
      setForm({ name: "", pin: "", role: "employee", area: "kitchen", station: "", location_ids: [] });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doReset = async (id) => {
    if (newPin.length < 4) return toast.error("PIN must be at least 4 digits");
    try {
      await api.post(`/staff/${id}/reset-pin`, { pin: newPin });
      toast.success("PIN reset");
      setResetId(null); setNewPin("");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-[#2E2E2E] bg-[#171717] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#171717] flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E]">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 text-[#E11D2A]" /> Manage Staff</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-xl border border-[#2E2E2E] bg-[#0E0E0E] p-4 space-y-3">
            <p className="font-medium text-sm">Add a staff member</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
              <input data-testid="staff-pin-input" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} placeholder="PIN (digits)" className="rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
              <select data-testid="staff-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]">
                <option value="employee">Employee</option>
                <option value="supervisor">Supervisor</option>
              </select>
              <select data-testid="staff-area-select" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} disabled={form.role === "supervisor"} className="rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A] disabled:opacity-50">
                <option value="kitchen">Kitchen</option>
                <option value="foh">Front of House</option>
              </select>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5">Locations (assign one or more)</p>
              <div className="flex flex-wrap gap-1.5">
                {locations.map((l) => (
                  <button key={l.id} type="button" data-testid={`staff-loc-${l.id}`} onClick={() => toggleLoc(l.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.location_ids.includes(l.id) ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#171717] border-[#2E2E2E] text-[#A3A3A3] hover:border-[#E11D2A]/50"}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="staff-add-button" onClick={add} disabled={busy} className="w-full rounded-lg bg-[#E11D2A] text-white font-semibold py-2.5 flex items-center justify-center gap-2 hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
              <Plus className="h-4 w-4" /> Add Staff
            </button>
          </div>

          <div className="space-y-2">
            {staff.map((s) => (
              <div key={s.id} className="rounded-lg border border-[#2E2E2E] bg-[#0E0E0E] px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{s.name}</p>
                    <p className="text-xs text-[#6B6B6B] capitalize">{s.role}{s.area && s.area !== "all" ? ` · ${s.area === "foh" ? "Front of House" : s.area}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-staff-button-${s.id}`} onClick={() => (editId === s.id ? (setEditId(null), setEdit(null)) : startEdit(s))} className="h-8 w-8 rounded-lg hover:bg-[#E11D2A]/15 flex items-center justify-center text-[#A3A3A3] hover:text-[#E11D2A] transition-colors" title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`reset-pin-button-${s.id}`} onClick={() => { setResetId(resetId === s.id ? null : s.id); setNewPin(""); }} className="h-8 w-8 rounded-lg hover:bg-[#E11D2A]/15 flex items-center justify-center text-[#A3A3A3] hover:text-[#E11D2A] transition-colors" title="Reset PIN">
                      <KeyRound className="h-4 w-4" />
                    </button>
                    {s.role !== "owner" && (
                      <button onClick={() => remove(s.id)} className="h-8 w-8 rounded-lg hover:bg-[#E11D2A]/15 flex items-center justify-center text-[#A3A3A3] hover:text-[#E11D2A] transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                {resetId === s.id && (
                  <div className="mt-3 flex gap-2">
                    <input data-testid="reset-pin-input" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} placeholder="New PIN (digits)" className="flex-1 rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2 text-sm outline-none focus:border-[#E11D2A]" />
                    <button data-testid="reset-pin-save-button" onClick={() => doReset(s.id)} className="rounded-lg bg-[#E11D2A] text-white font-semibold px-4 text-sm hover:bg-[#B91C1C] transition-colors">Save</button>
                  </div>
                )}
                {editId === s.id && edit && (
                  <div className="mt-3 space-y-2.5">
                    <input data-testid="edit-name-input" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} placeholder="Name" className="w-full rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2 text-sm outline-none focus:border-[#E11D2A]" />
                    {s.role !== "owner" && (
                      <select data-testid="edit-role-select" value={edit.role} onChange={(e) => setEdit((p) => ({ ...p, role: e.target.value }))} className="w-full rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2 text-sm outline-none focus:border-[#E11D2A]">
                        <option value="employee">Employee</option>
                        <option value="supervisor">Supervisor</option>
                      </select>
                    )}
                    {edit.role === "employee" && (
                      <select data-testid="edit-area-select" value={edit.area} onChange={(e) => setEdit((p) => ({ ...p, area: e.target.value }))} className="w-full rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2 text-sm outline-none focus:border-[#E11D2A]">
                        <option value="kitchen">Kitchen</option>
                        <option value="foh">Front of House</option>
                      </select>
                    )}
                    {s.role !== "owner" && (
                      <div className="flex flex-wrap gap-1.5">
                        {locations.map((l) => (
                          <button key={l.id} type="button" data-testid={`edit-loc-${l.id}`} onClick={() => toggleEditLoc(l.id)} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${edit.location_ids.includes(l.id) ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#171717] border-[#2E2E2E] text-[#A3A3A3] hover:border-[#E11D2A]/50"}`}>{l.name}</button>
                        ))}
                      </div>
                    )}
                    <button data-testid="edit-save-button" onClick={() => saveEdit(s.id)} className="w-full rounded-lg bg-[#E11D2A] text-white font-semibold py-2 text-sm hover:bg-[#B91C1C] transition-colors">Save changes</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
